/**
 * mergeAnonymousCart — copies items from an anonymous cart into a target
 * (authenticated) cart by calling CartService.addItemToCart per item.
 *
 * Framework-agnostic: contains no framework-specific dependencies.
 */

import { CartService } from '@propeller-commerce/propeller-sdk-v2';
import type {
  Cart,
  CartMainItem,
  GraphQLClient,
  MediaImageProductSearchInput,
  TransformationsInput,
} from '@propeller-commerce/propeller-sdk-v2';

export interface MergeAnonymousCartConfig {
  graphqlClient: GraphQLClient;
  /** The authenticated user's cart that anonymous items will be added to. */
  targetCartId: string;
  /** The anonymous cart from the store/state captured before authentication. */
  anonymousCart: Cart | null;
  language: string;
  imageSearchFilters: MediaImageProductSearchInput;
  imageVariantFilters: TransformationsInput;
}

export async function mergeAnonymousCart(
  cfg: MergeAnonymousCartConfig,
): Promise<Cart | null> {
  const items = cfg.anonymousCart?.items ?? [];
  if (!items.length) return null;
  // Skip if anonymous cart IS the target — guards against self-merge.
  if (
    cfg.anonymousCart?.cartId &&
    cfg.anonymousCart.cartId === cfg.targetCartId
  ) {
    return null;
  }

  const service = new CartService(cfg.graphqlClient);
  let result: Cart | null = null;

  // Serial iteration: each call returns the full cart, so parallel races.
  for (const item of items as CartMainItem[]) {
    if (!item.productId || !item.quantity) continue;

    // Resolve clusterId from either the top-level field or the nested cluster
    // object — depending on how the item was originally added (PDP vs. cluster
    // page) and how the cart was serialised, the field can live in either spot.
    const itemAny = item as any;
    const resolvedClusterId =
      itemAny.clusterId ??
      itemAny.cluster?.clusterId ??
      itemAny._clusterId ??
      itemAny._cluster?._clusterId ??
      itemAny._cluster?.clusterId;

    const childItems = item.childItems
      ?.filter((c: any) => c.productId)
      .map((c: any) => ({
        productId: c.productId,
        quantity: c.quantity ?? item.quantity,
      }));

    try {
      result = await service.addItemToCart({
        id: cfg.targetCartId,
        input: {
          productId: item.productId,
          quantity: item.quantity,
          ...(resolvedClusterId !== undefined &&
            resolvedClusterId !== null && { clusterId: resolvedClusterId }),
          ...(childItems && childItems.length > 0 && { childItems }),
          ...(item.notes && { notes: item.notes }),
        },
        language: cfg.language,
        imageSearchFilters: cfg.imageSearchFilters,
        imageVariantFilters: cfg.imageVariantFilters,
      });
    } catch (e) {
      console.error(
        '[mergeAnonymousCart] addItemToCart failed for productId=' +
          item.productId,
        e,
      );
    }
  }

  return result;
}
